import React from 'react';

import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {SkeletonItem} from 'src/components/skeleton';
import {ResetDefaultModulesSection} from 'src/modules/create-account/steps/license-create/components/reset-default-settings';

import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';

import {DataSetsSection} from './data-sets/data-sets-section';
import {PlatformFeatureModulesSection} from './platform-feature-modules/platform-feature-modules-section';
import {ExportOptionsSection} from './platform-features/export-options-section';
import {PluginsAppsSection} from './platform-features/plugins-apps-section';

import type {TreeStructure} from '@pb/kit-component-tree-v2';
import type {LicenseType} from 'src/openapi/admin/models/LicenseType';

type Props = {
    disabled?: boolean,
    licenseType?: LicenseType,
    isDefaultModules: boolean,

    dataSetsLoading: boolean,
    dataSetsTree: TreeStructure | null,
    dataSets: Array<string>,
    setDataSets: (dataSets: Array<string>) => void,

    platformFeatureModulesLoading: boolean,
    platformFeatureModulesTree: TreeStructure | null,
    platformFeatureModules: Array<string>,
    setPlatformFeatureModules: (platformFeatureModules: Array<string>) => void,

    platformFeaturesLoading: boolean,
    pluginsAppsTree: TreeStructure | null,
    exportOptionsTree: TreeStructure | null,
    platformFeatures: Array<string>,
    setPlatformFeatures: (platformFeatures: Array<string>) => void,

    onFieldChange: (parameterName: string) => void,
    onResetModules: () => void,
};

export const AccessToModulesSection: React.FC<Props> = ({
    disabled,
    licenseType,
    isDefaultModules,

    dataSetsLoading,
    dataSetsTree,
    dataSets,
    setDataSets,

    platformFeatureModulesLoading,
    platformFeatureModulesTree,
    platformFeatureModules,
    setPlatformFeatureModules,

    platformFeaturesLoading,
    pluginsAppsTree,
    exportOptionsTree,
    platformFeatures,
    setPlatformFeatures,

    onFieldChange,
    onResetModules,
}) => (
    <FormSection
        headline="Module Access Permissions"
        info={headline => (dataSetsLoading
            ? <SkeletonItem width={220} height={28}/>
            : headline
        )}>
        <ResetDefaultModulesSection
            isDefaultModules={isDefaultModules}
            loading={dataSetsLoading || platformFeatureModulesLoading || platformFeaturesLoading}
            licenseType={licenseType}
            disabled={disabled}
            onReset={onResetModules}
        />
        <br/>
        <Group gap="xxl" alignItems="start">
            <DataSetsSection
                dataSetsTree={dataSetsTree}
                loading={dataSetsLoading}
                dataSetsIds={dataSets}
                onChange={(newDataSets: Array<string>) => {
                    onFieldChange?.('Standard Data Sets');
                    setDataSets(newDataSets);
                }}
            />
            <PlatformFeatureModulesSection {...{
                loading: platformFeatureModulesLoading,
                platformFeatureModulesTree,
                platformFeatureModules,
                onChange: (newPlatformFeatureModules: Array<string>) => {
                    onFieldChange?.('Platform Feature');
                    setPlatformFeatureModules(newPlatformFeatureModules);
                },
            }}
            />
            <ResponsiveElementGroup>
                <PluginsAppsSection {...{
                    loading: platformFeaturesLoading,
                    pluginsAppsTree,
                    platformFeatures,
                    onChange: (newPlatformFeatures: Array<string>) => {
                        onFieldChange?.('Plugins and Apps');
                        setPlatformFeatures(newPlatformFeatures);
                    },
                }}
                />
                <ExportOptionsSection {...{
                    loading: platformFeaturesLoading,
                    exportOptionsTree,
                    platformFeatures,
                    onChange: (newExportOptions: Array<string>) => {
                        onFieldChange?.('Export Options');
                        setPlatformFeatures(newExportOptions);
                    },
                }}
                />
            </ResponsiveElementGroup>
        </Group>
    </FormSection>
);
